import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, MapPin, Calendar, Clock, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";

const STATUS_STYLES = {
  scheduled: "bg-amber-100 text-amber-700 border-amber-200",
  assigned: "bg-sky/15 text-sky border-sky/30",
  in_progress: "bg-violet-100 text-violet-700 border-violet-200",
  completed: "bg-crop/15 text-crop border-crop/30",
  invoiced: "bg-orange-100 text-orange-700 border-orange-200",
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

export default function PilotDashboard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const user = (() => { try { return JSON.parse(localStorage.getItem("aero_user") || "{}"); } catch { return {}; } })();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pilot/work-orders", { params: filter !== "all" ? { status_filter: filter } : {} });
      setJobs(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("aero_token");
        navigate("/login");
      } else toast.error("Failed to load jobs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const logout = () => {
    localStorage.removeItem("aero_token");
    localStorage.removeItem("aero_user");
    navigate("/login");
  };

  const todayCount = jobs.filter((j) => {
    if (!j.scheduled_date) return false;
    return j.scheduled_date.slice(0, 10) === new Date().toISOString().slice(0, 10);
  }).length;

  return (
    <div data-testid="pilot-dashboard" className="min-h-screen bg-muted/30">
      <header className="bg-card border-b border-border sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-display text-lg font-bold text-primary">AeroAcres</Link>
            <Badge className="bg-accent/15 text-accent border-0">Pilot</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-muted-foreground">{user?.name || user?.email}</span>
            <Button onClick={logout} variant="outline" size="sm" data-testid="logout-btn" className="rounded-full">
              <LogOut className="h-4 w-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-display text-3xl md:text-4xl text-primary font-bold">Hello, {user?.name?.split(" ")[0] || "Pilot"} 👋</h1>
          <p className="text-muted-foreground mt-1">
            {todayCount > 0
              ? <>You have <span className="font-semibold text-primary">{todayCount} job{todayCount !== 1 ? "s" : ""}</span> scheduled for today.</>
              : "No jobs scheduled for today. Here are all your assignments:"}
          </p>
        </div>

        <Tabs value={filter} onValueChange={setFilter} className="mb-5">
          <TabsList data-testid="pilot-status-tabs" className="w-full overflow-x-auto">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="assigned">Upcoming</TabsTrigger>
            <TabsTrigger value="in_progress">In progress</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : jobs.length === 0 ? (
          <div data-testid="pilot-empty" className="py-16 text-center bg-card border border-border rounded-3xl">
            <div className="text-5xl">🚁</div>
            <div className="mt-3 font-display text-lg text-primary">No jobs to show</div>
            <p className="text-sm text-muted-foreground mt-1">Your admin will assign new jobs to you here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((j, i) => (
              <Link key={j.id} to={`/pilot/jobs/${j.id}`} data-testid={`pilot-job-${i}`}
                className="block bg-card border border-border rounded-2xl shadow-card hover:shadow-card-hover hover:border-accent/40 transition-all p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`border text-xs ${STATUS_STYLES[j.status] || "bg-muted text-foreground"}`}>{j.status.replace("_", " ")}</Badge>
                      {j.priority === "rush" && <Badge className="bg-accent/15 text-accent border-accent/30 text-xs">RUSH</Badge>}
                    </div>
                    <div className="mt-2 font-display text-lg font-bold text-primary">{j.client_name}{j.farm_name && <span className="text-muted-foreground font-normal"> · {j.farm_name}</span>}</div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" /> {j.location_address}</span>
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {j.scheduled_date || "No date set"}</span>
                      <span>{j.service}{j.acres ? ` · ${j.acres} acres` : ""}</span>
                      {j.check_in_at && (
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Started {new Date(j.check_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground self-center" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
